import { type AUTH_CONNECTION_TYPE } from "@web3auth/auth";
import { jwtDecode } from "jwt-decode";
import type { LocationQuery } from "vue-router";
import { Action, getModule, Module, Mutation, VuexModule } from "vuex-module-decorators";

import config from "@/config";
import { AGENTIC_LOGIN_METHOD } from "@/helpers/analytics";
import auth0Handler from "@/helpers/auth/auth0Handler";
import { signedPost } from "@/helpers/http";
import log from "@/helpers/log";
import { syncStoredUserConsent } from "@/helpers/userConsentPersistence";
import { setAccessToken } from "@/utils/accessToken";
import { GROUPED_AUTH_CONNECTION_ID_LOGIN_TYPE_MAP, TOAST_TYPES, type ToastMessage, USER_MODULE_KEY, type UserProfile } from "@/utils/enums";
import type { DashboardAuthUser } from "@/utils/interface";
import store from "@/vuexStore";

import { authGet, authPatch, authPost } from "../authStoreRequest";
import {
  clearAuthStorage,
  clearStoredAuthConnection,
  getBridgeAuthToken,
  getStoredAuthConnection,
  setBridgeAuthToken,
  setStoredAuthConnection,
} from "./authStorage";
import downloadsModule, { clearDownloadsStorage } from "./downloads";

export { AUTH_CONNECTION_STORAGE_KEY } from "./authStorage";

// @ts-expect-error decorator is picking the wrong overload as its type
@Module({
  namespaced: true,
  name: USER_MODULE_KEY,
  store,
  dynamic: true,
})
class UserModule extends VuexModule {
  userInfo: DashboardAuthUser | undefined = undefined;

  userProfile: UserProfile = {};

  showMobileDrawer = false;

  public toastMessage: ToastMessage = {
    message: "",
    type: TOAST_TYPES.INFO,
  };

  public get userInfoGetter() {
    return this.userInfo;
  }

  public get userProfileGetter() {
    return this.userProfile;
  }

  public get userId() {
    return this.userInfo ? `${this.userInfo.verifierId}:${this.userInfo.sub}` : undefined;
  }

  public get typeOfLogin() {
    const verifier = this.userProfile.verifier ?? this.userInfo?.verifier;
    return GROUPED_AUTH_CONNECTION_ID_LOGIN_TYPE_MAP[verifier ?? ""] ?? "";
  }

  @Mutation
  public setUserInfo(userInfo: DashboardAuthUser | undefined) {
    this.userInfo = userInfo;
  }

  @Mutation
  public setUserProfile(userProfile: UserProfile) {
    this.userProfile = userProfile;
  }

  @Action
  async triggerLogin({
    authConnection,
    query,
  }: {
    authConnection: AUTH_CONNECTION_TYPE | string;
    authConnectionId?: string;
    groupedAuthConnectionId?: string;
    query: LocationQuery;
    idToken?: string;
  }): Promise<void> {
    try {
      // Mirror the connection to localStorage before redirecting: appState is the primary carrier
      // through the Auth0 round-trip, but the SPA SDK transaction cache can be cleared before /end
      // (Safari ITP, etc.), so handleRedirectCallback falls back to this stored value.
      setStoredAuthConnection(authConnection);
      await auth0Handler.login(authConnection, {
        appState: { query },
      });
    } catch (error) {
      log.error("Login error: ", error);
      throw error;
    }
  }

  @Action
  async authenticateBridgeToken(bridgeToken: string): Promise<void> {
    setBridgeAuthToken(bridgeToken);
    clearStoredAuthConnection();

    let sub = "";
    let email: string | undefined;
    try {
      const decoded = jwtDecode<{ sub?: string; email?: string }>(bridgeToken);
      sub = decoded.sub ?? "";
      email = decoded.email;
    } catch {
      throw new Error("Invalid bridge auth token");
    }
    if (!sub) {
      throw new Error("Bridge auth token is missing sub");
    }

    this.setUserInfo({
      authConnection: AGENTIC_LOGIN_METHOD.QR,
      verifier: config.agenticAuthGroupedConnectionId,
      verifierId: sub.toLowerCase(),
      sub,
      email,
    });
  }

  @Action
  async enableMFA(): Promise<void> {
    log.warn("Web3Auth MFA is not available with Auth0 login");
  }

  @Action
  async manageMFA(): Promise<void> {
    log.warn("Web3Auth MFA is not available with Auth0 login");
  }

  @Action
  async rehydrateSession(): Promise<boolean> {
    const bridgeToken = getBridgeAuthToken();
    if (bridgeToken) {
      try {
        await this.authenticateBridgeToken(bridgeToken);
        return true;
      } catch {
        clearAuthStorage();
        return false;
      }
    }

    const storedConnection = getStoredAuthConnection();
    const userInfo = await auth0Handler.rehydrateSession(storedConnection);
    if (!userInfo) {
      log.info("No Auth0 session found");
      return false;
    }

    this.setUserInfo(userInfo);
    return true;
  }

  /** @deprecated Use rehydrateSession */
  @Action
  async rehydrateWallet(): Promise<boolean> {
    return this.rehydrateSession();
  }

  @Action
  async authenticateWithDevDashboard() {
    try {
      const response = await signedPost(`${config.backendURL}/user/authenticate`, {}, {});
      const { token } = response as { token: string };
      setAccessToken(token);
      void syncStoredUserConsent();
      return response;
    } catch (error) {
      const err = error as { code: number; message: string };
      if (err.code === 404) {
        return null;
      }
      throw error;
    }
  }

  @Action
  async logout(options: { returnTo?: string } = {}): Promise<boolean> {
    try {
      const bridgeToken = getBridgeAuthToken();
      // Stop all in-flight polls and clear Vuex state first so no loop can
      // write back to localStorage after we remove the key below.
      downloadsModule.clearAll();
      // clearDownloadsStorage removes the persisted key and resets activeUserId
      // so persistEntries() becomes a no-op for any stale async continuation.
      // Must run after clearAll() but before clearAuthStorage() wipes userId.
      clearDownloadsStorage(this.userId ?? "");
      clearAuthStorage();
      setAccessToken(null);
      this.setUserInfo(undefined);

      if (bridgeToken) {
        return false;
      }

      await auth0Handler.logout(options);
      return true;
    } catch (error) {
      log.error("Logout error: ", error);
      return false;
    }
  }

  @Action
  async getUserProfile() {
    try {
      const response = (await authGet(`${config.backendURL}/user`, {}, {})) as { code: number; user: UserProfile };
      const { code, user } = response;
      if (code === 200) {
        this.setUserProfile(user);
      }
      return response;
    } catch (err) {
      log.error("error when getting user profile", err);
      throw err;
    }
  }

  @Action
  async recordLogin({
    hostName,
    os,
    osVersion,
    browser,
    browserVersion,
    platform,
    authConnection,
  }: {
    hostName: string;
    os: string;
    osVersion: string;
    browser: string;
    browserVersion: string;
    platform: string;
    authConnection: string;
  }) {
    try {
      const payload = {
        host_name: hostName,
        os,
        os_version: osVersion,
        browser,
        browser_version: browserVersion,
        platform,
        auth_connection: authConnection,
      };
      const response = await authPost(`${config.backendURL}/user/record-login`, payload, {});
      return response;
    } catch (err) {
      return err;
    }
  }

  @Action
  async createProfile({ name, email }: { name: string; email: string }) {
    const userProfile = {
      name,
      email,
    };
    const response = await signedPost(`${config.backendURL}/user/profile`, userProfile, {});
    const { token } = response as { token: string };
    setAccessToken(token);
    void syncStoredUserConsent();
    await this.getUserProfile();
  }

  @Action
  async updateUserProfile({
    name,
    email,
    country,
    jobRole,
    acceptedTermsAndPrivacyPolicy,
  }: {
    name?: string;
    email?: string;
    country?: string;
    jobRole?: string;
    acceptedTermsAndPrivacyPolicy?: boolean;
  }) {
    try {
      const data = {
        name,
        email,
        country,
        job_role: jobRole,
        accepted_terms_and_privacy_policy: acceptedTermsAndPrivacyPolicy,
      };
      await authPatch(`${config.backendURL}/user`, data, {});
      await this.getUserProfile();
    } catch (err) {
      log.error("error when updating user profile", err);
      throw err;
    }
  }

  @Mutation
  public setMobileDrawer() {
    this.showMobileDrawer = !this.showMobileDrawer;
  }

  @Mutation
  public closeMobileDrawer() {
    this.showMobileDrawer = false;
  }

  @Mutation
  public setToastMessage(details: ToastMessage) {
    this.toastMessage = details;
  }
}

const userModule = getModule(UserModule);

export default userModule;
